// src/App.js
import "./index.css"; // Tailwind + globals (base)
import "./App.css";   // Custom marketing + ByOnco overrides
import "@byonco/tokens/tokens.css";
import "./styles/tokens.css";
import "./styles/ease-health.css";
import "./components/brand/brand.css";

import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import CookieConsent from "./components/CookieConsent";
import HomeLogo from "./components/layout/HomeLogo";
import VaniSplash from "./components/VaniSplash";

// Pages
import Security from "./pages/Security";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import Careers from "./pages/Careers";
import GetStarted from "./pages/GetStarted";
import GetMatched from "./pages/GetMatched";
import CancellationRefund from "./pages/CancellationRefund";
import ShippingDelivery from "./pages/ShippingDelivery";
import MedicalDisclaimer from "./pages/MedicalDisclaimer";
import ConsumerHealthPrivacy from "./pages/ConsumerHealthPrivacy";

// Med tourism landing (new main home)
import MedTourismLanding from "./pages/MedTourismLanding";
import VaniLanding from "./pages/VaniLanding";
import ForHospitalsPage from "./pages/ForHospitalsPage";
import TalkPage from "./pages/TalkPage";
import SpecialDayPage from "./pages/SpecialDayPage";
import VoiceLayer from "./voice/VoiceLayer";
import FindHospitalsPage from "./pages/FindHospitalsPage";
import RareCancersPage from "./pages/RareCancersPage";
import FindOncologistsPage from "./pages/FindOncologistsPage";
import FindOncologistsStatePage from "./pages/FindOncologistsStatePage";
import FindOncologistsCityPage from "./pages/FindOncologistsCityPage";
import TeleconsultationPage from "./pages/TeleconsultationPage";
import CostCalculatorPage from "./pages/CostCalculatorPage";
import JourneyBuilderPage from "./pages/JourneyBuilderPage";
import JourneyPlanDetails from "./pages/JourneyPlanDetails";
import TreatmentOptionsPage from "./pages/TreatmentOptionsPage";
import SecondOpinionPromptPage from "./pages/SecondOpinionPromptPage";
import SignInPage from "./pages/SignInPage";
import ProfilePage from "./pages/ProfilePage";
import MedicalTourismWaitlistPage from "./pages/MedicalTourismWaitlistPage";
import PaymentTestPage from "./pages/PaymentTestPage";
import ProtectedRoute from "./components/ProtectedRoute";
import SubscriptionGuard from "./components/SubscriptionGuard";
import SubscriptionExpiryWarning from "./components/SubscriptionExpiryWarning";
import SubscribePage from "./pages/SubscribePage";
import LaunchOfferPage from "./pages/LaunchOfferPage";
import LifetimeDrawPage from "./pages/LifetimeDrawPage";
import AboutPage from "./pages/AboutPage";
import CancerHub from "./pages/CancerHub";
import CancerPage from "./pages/CancerPage";
import USStateHub from "./pages/USStateHub";
import USStatePage from "./pages/USStatePage";
import FindCancerCentersHub from "./pages/FindCancerCentersHub";
import CancerCenterDetailPage from "./pages/CancerCenterDetailPage";
import RareCancerDetailPage from "./pages/RareCancerDetailPage";
import BestCancerNavigationPlatform from "./pages/BestCancerNavigationPlatform";
import BestMedicalTourismOncology from "./pages/BestMedicalTourismOncology";
import BestCancerApp from "./pages/BestCancerApp";
import CancerSecondOpinionHub from "./pages/CancerSecondOpinionHub";
import CancerSecondOpinionSpoke from "./pages/CancerSecondOpinionSpoke";
import CancerTeleconsultationHub from "./pages/CancerTeleconsultationHub";
import CancerTeleconsultationSpoke from "./pages/CancerTeleconsultationSpoke";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import NotFoundPage from "./pages/NotFoundPage";
import GlobalErrorBoundary from "./components/GlobalErrorBoundary";
import { memberAppRoute } from "./app/MemberRoutes";
import { isMemberAppPath, isOncoAgentAppPath } from "./config/hosts";
import { oncoAgentAppRoute, oncoAgentLegacyRedirects } from "./oncoagent/app/OncoAgentAppRoutes";

// OncoAgent pages - use dynamic import with Suspense for better error handling
const OncoAgentLanding = lazy(() =>
  import("./oncoagent")
    .then((mod) => ({ default: mod.OncoAgentLanding }))
    .catch((err) => {
      console.error("Failed to load OncoAgentLanding:", err);
      return {
        default: () => (
          <div className="min-h-screen bg-cream-paper flex items-center justify-center p-6">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-2">OncoAgent Not Available</h2>
              <p className="text-gray-600 mb-4">The OncoAgent module failed to load.</p>
              <p className="text-xs text-gray-500 mb-4">Error: {err?.message || "Unknown error"}</p>
              <a href="/oncoagent/diagnostics" className="text-blue-600 underline">
                View Diagnostics
              </a>
            </div>
          </div>
        ),
      };
    })
);

const OncoAgentNewCase = lazy(() =>
  import("./oncoagent")
    .then((mod) => ({ default: mod.OncoAgentNewCase }))
    .catch((err) => {
      console.error("Failed to load OncoAgentNewCase:", err);
      return {
        default: () => (
          <div className="min-h-screen bg-cream-paper flex items-center justify-center p-6">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-2">OncoAgent Not Available</h2>
              <p className="text-gray-600 mb-4">The OncoAgent module failed to load.</p>
              <a href="/oncoagent/diagnostics" className="text-blue-600 underline">
                View Diagnostics
              </a>
            </div>
          </div>
        ),
      };
    })
);

const OncoAgentTrack = lazy(() =>
  import("./oncoagent")
    .then((mod) => ({ default: mod.OncoAgentTrack }))
    .catch((err) => {
      console.error("Failed to load OncoAgentTrack:", err);
      return {
        default: () => (
          <div className="min-h-screen bg-cream-paper flex items-center justify-center p-6">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Tracking Not Available</h2>
              <p className="text-gray-600 mb-4">The patient tracking page failed to load.</p>
            </div>
          </div>
        ),
      };
    })
);

const OncoAgentDashboard = lazy(() =>
  import("./oncoagent")
    .then((mod) => ({ default: mod.OncoAgentDashboard }))
    .catch((err) => {
      console.error("Failed to load OncoAgentDashboard:", err);
      return {
        default: () => (
          <div className="min-h-screen bg-cream-paper flex items-center justify-center p-6">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-2">OncoAgent Not Available</h2>
              <p className="text-gray-600 mb-4">The OncoAgent module failed to load.</p>
              <a href="/oncoagent/diagnostics" className="text-blue-600 underline">
                View Diagnostics
              </a>
            </div>
          </div>
        ),
      };
    })
);

// Error boundary - use lazy loading but wrap in a synchronous fallback
// Error boundaries must be class components and loaded synchronously
const OncoAgentErrorBoundaryLazy = lazy(() =>
  import("./oncoagent")
    .then((mod) => ({ default: mod.OncoAgentErrorBoundary }))
    .catch(() => ({
      default: class FallbackErrorBoundary extends React.Component {
        constructor(props) {
          super(props);
          this.state = { hasError: false };
        }
        static getDerivedStateFromError() {
          return { hasError: true };
        }
        componentDidCatch(error, errorInfo) {
          console.error("OncoAgent Error:", error, errorInfo);
        }
        render() {
          if (this.state.hasError) {
            return (
              <div className="min-h-screen bg-cream-paper flex items-center justify-center p-6">
                <div className="text-center">
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
                  <p className="text-gray-600 mb-4">Please try again or start over.</p>
                  <button
                    onClick={() => this.setState({ hasError: false })}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            );
          }
          return this.props.children;
        }
      },
    }))
);

// Wrapper component to use lazy error boundary with Suspense
function OncoAgentErrorBoundary({ children }) {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-cream-paper flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
            <p className="text-gray-600">Loading…</p>
          </div>
        </div>
      }
    >
      <OncoAgentErrorBoundaryLazy>{children}</OncoAgentErrorBoundaryLazy>
    </Suspense>
  );
}

const OncoAgentDiagnostics = lazy(() =>
  import("./oncoagent")
    .then((mod) => ({ default: mod.OncoAgentDiagnostics || (() => <div>Diagnostics not available</div>) }))
    .catch(() => ({
      default: () => (
        <div className="min-h-screen bg-cream-paper flex items-center justify-center p-6">
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Diagnostics Not Available</h2>
            <p className="text-gray-600">OncoAgent module not loaded.</p>
          </div>
        </div>
      ),
    }))
);

/* -------------------------------------------------------
   Scroll to top on route change (unless a hash targets a section)
------------------------------------------------------- */
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
}

/* -------------------------------------------------------
   Global hash scroller (unchanged)
------------------------------------------------------- */
function HashScroller({ offset = 96 }) {
  const location = useLocation();

  useEffect(() => {
    const { hash } = location;
    if (!hash) return;

    const wanted = hash.slice(1);

    const tryScroll = () => {
      const el = document.getElementById(wanted);

      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: "smooth" });
      }
    };

    const t = setTimeout(tryScroll, 80);
    return () => clearTimeout(t);
  }, [location, offset]);

  return null;
}

/* -------------------------------------------------------
   App Component (routing)
------------------------------------------------------- */
export default function App() {
  const location = useLocation();
  const path = (location.pathname || "/").replace(/\/+$/, "") || "/";
  const isSpecialDay = path === "/specialday";
  const isMemberApp = isMemberAppPath(path);
  const isOncoAgentApp = isOncoAgentAppPath(path);
  const isTalkSurface = path === "/talk";

  return (
    <>
      {isSpecialDay || isMemberApp || isOncoAgentApp ? null : <SubscriptionExpiryWarning />}
      {isSpecialDay || isMemberApp || isOncoAgentApp ? null : <HomeLogo />}
      {isSpecialDay || isMemberApp || isOncoAgentApp || isTalkSurface ? null : <VoiceLayer />}
      <AnimatePresence mode="wait">
        <GlobalErrorBoundary>
          <Suspense
          fallback={
            <div className="page-shell min-h-screen flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0f3e17] mx-auto mb-3" />
                <p className="text-[#222222]">Loading…</p>
              </div>
            </div>
          }
        >
          <ScrollToTop />
          <HashScroller offset={96} />

          <Routes location={location} key={isMemberApp ? '/app' : isOncoAgentApp ? '/oncoagent/app' : location.pathname}>
            {/* MAIN HOME, Vani marketing. MedTourismLanding kept at /platform. */}
            <Route
              path="/"
              element={
                <motion.div
                  className="page-shell min-h-screen text-ink"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <VaniLanding />
                </motion.div>
              }
            />
            <Route
              path="/platform"
              element={
                <motion.div
                  className="page-shell min-h-screen text-gray-900"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <MedTourismLanding />
                </motion.div>
              }
            />
            <Route
              path="/talk"
              element={
                <div className="page-shell talk-route">
                  <TalkPage />
                </div>
              }
            />
            <Route
              path="/specialday"
              element={
                <div className="page-shell">
                  <SpecialDayPage />
                </div>
              }
            />
            <Route
              path="/for-hospitals"
              element={
                <motion.div
                  className="page-shell min-h-screen"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <ForHospitalsPage />
                </motion.div>
              }
            />

            {/* Classic old ByOnco homepage */}
            {/* Classic old ByOnco homepage route removed to hide legacy landing page */}

            {/* ----------- Core ByOnco flows ----------- */}
            <Route
              path="/get-started"
              element={
                <ProtectedRoute>
                  <motion.div
                    className="page-shell min-h-screen bg-cream-paper text-charcoal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <GetStarted />
                  </motion.div>
                </ProtectedRoute>
              }
            />
            <Route
              path="/get-matched"
              element={
                <motion.div
                  className="page-shell min-h-screen"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <GetMatched />
                </motion.div>
              }
            />
            <Route path="/join-waitlist" element={<Navigate to="/authentication" replace />} />
            <Route path="/waitlist" element={<Navigate to="/authentication" replace />} />

            {/* Short aliases, canonicals stay on the long paths. Vercel 308s too. */}
            <Route path="/hospitals" element={<Navigate to="/find-hospitals" replace />} />
            <Route path="/cost" element={<Navigate to="/cost-calculator" replace />} />
            <Route path="/pricing" element={<Navigate to="/subscribe" replace />} />

            {/* ----------- Subscribe ----------- */}
            <Route
              path="/lifetime-draw"
              element={
                <motion.div
                  className="page-shell min-h-screen"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <LifetimeDrawPage />
                </motion.div>
              }
            />
            <Route
              path="/start"
              element={
                <motion.div
                  className="page-shell min-h-screen"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <LaunchOfferPage />
                </motion.div>
              }
            />
            <Route
              path="/subscribe"
              element={
                <motion.div
                  className="page-shell min-h-screen"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <SubscribePage />
                </motion.div>
              }
            />

            {/* ----------- Auth ----------- */}
            <Route
              path="/auth"
              element={<Navigate to="/authentication" replace />}
            />
            <Route
              path="/authentication"
              element={
                <motion.div
                  className="min-h-screen"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <SignInPage />
                </motion.div>
              }
            />
            <Route path="/dashboard" element={<Navigate to="/app" replace />} />
            {memberAppRoute()}
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route
              path="/reset-password"
              element={
                <div className="min-h-screen bg-cream-paper text-charcoal">
                  <ResetPasswordPage />
                </div>
              }
            />

            {/* ----------- Cancer SEO hub & US State Directory ----------- */}
            <Route path="/cancer" element={<CancerHub />} />
            <Route path="/cancer/:cancerType" element={<CancerPage />} />
            <Route path="/cancer-centers" element={<Navigate to="/find-cancer-centers" replace />} />
            <Route path="/cancer-centers-by-state" element={<USStateHub />} />
            <Route path="/cancer-centers/:stateSlug" element={<USStatePage />} />
            <Route path="/find-cancer-centers" element={<FindCancerCentersHub />} />
            <Route path="/find-cancer-centers/:centerSlug" element={<CancerCenterDetailPage />} />

            {/* ----------- Dedicated Target Keyword SEO Pages ----------- */}
            <Route path="/best-cancer-navigation-platform" element={<BestCancerNavigationPlatform />} />
            <Route path="/top-cancer-navigation-platform" element={<Navigate to="/best-cancer-navigation-platform" replace />} />
            <Route path="/cancer-navigation-platform" element={<Navigate to="/best-cancer-navigation-platform" replace />} />

            <Route path="/best-medical-tourism-oncology" element={<BestMedicalTourismOncology />} />
            <Route path="/leading-medical-tourism-oncology-platform" element={<Navigate to="/best-medical-tourism-oncology" replace />} />
            <Route path="/medical-tourism-cancer" element={<Navigate to="/best-medical-tourism-oncology" replace />} />

            <Route path="/best-cancer-app" element={<BestCancerApp />} />
            <Route path="/cancer-app" element={<Navigate to="/best-cancer-app" replace />} />
            <Route path="/best-cancer-ai-agent" element={<Navigate to="/best-cancer-app" replace />} />
            <Route path="/cancer-app-best" element={<Navigate to="/best-cancer-app" replace />} />

            {/* ----------- Cancer Second Opinion Hub + Spokes (Cluster A) ----------- */}
            <Route path="/cancer-second-opinion" element={<CancerSecondOpinionHub />} />
            <Route path="/cancer-second-opinion/:spokeSlug" element={<CancerSecondOpinionSpoke />} />

            {/* ----------- Cancer Teleconsultation Hub + Spokes (Cluster B) ----------- */}
            <Route path="/cancer-teleconsultation" element={<CancerTeleconsultationHub />} />
            <Route path="/cancer-teleconsultation/:spokeSlug" element={<CancerTeleconsultationSpoke />} />
            <Route path="/video-consultation-oncologist" element={<CancerTeleconsultationSpoke slugOverride="video-consultation-oncologist" />} />
            <Route path="/what-to-expect-oncology-video-visit" element={<CancerTeleconsultationSpoke slugOverride="what-to-expect-oncology-video-visit" />} />

            {/* ----------- Profile ----------- */}
            <Route
              path="/profile"
              element={
                <motion.div
                  className="page-shell min-h-screen text-gray-900"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <ProfilePage />
                </motion.div>
              }
            />

            {/* ----------- Payment Test Page ----------- */}
            <Route
              path="/payment-test"
              element={
                <motion.div
                  className="page-shell min-h-screen text-gray-900"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <PaymentTestPage />
                </motion.div>
              }
            />

            {/* ----------- Waitlist ----------- */}
            <Route
              path="/waitlist/medical-tourism"
              element={
                <ProtectedRoute>
                  <motion.div
                    className="page-shell min-h-screen text-gray-900"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <MedicalTourismWaitlistPage />
                  </motion.div>
                </ProtectedRoute>
              }
            />

            {/* ----------- New Medical Tourism Pages ----------- */}
            <Route
              path="/find-hospitals"
              element={
                <motion.div
                  className="page-shell min-h-screen text-gray-900"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <FindHospitalsPage />
                </motion.div>
              }
            />

            {/* ----------- Find Oncologists Directory (Cluster C) ----------- */}
            <Route path="/find-oncologists" element={<FindOncologistsPage />} />
            <Route path="/find-oncologists/:stateSlug" element={<FindOncologistsStatePage />} />
            <Route path="/find-oncologists/:stateSlug/:citySlug" element={<FindOncologistsCityPage />} />

            <Route
              path="/rare-cancers"
              element={
                <motion.div
                  className="page-shell min-h-screen text-gray-900"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <RareCancersPage />
                </motion.div>
              }
            />
            <Route path="/rare-cancers/:cancerSlug" element={<RareCancerDetailPage />} />
            <Route path="/rare-cancer/:cancerSlug" element={<RareCancerDetailPage />} />

            <Route
              path="/second-opinion"
              element={
                <motion.div
                  className="page-shell min-h-screen text-gray-900"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <SecondOpinionPromptPage />
                </motion.div>
              }
            />
            <Route path="/second-opinion/*" element={<Navigate to="/second-opinion" replace />} />

            <Route
              path="/teleconsultation"
              element={
                <motion.div
                  className="page-shell min-h-screen text-gray-900"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <TeleconsultationPage />
                </motion.div>
              }
            />

            <Route
              path="/cost-calculator"
              element={
                <motion.div
                  className="page-shell min-h-screen text-gray-900"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <CostCalculatorPage />
                </motion.div>
              }
            />

            <Route
              path="/journey-builder"
              element={
                <motion.div
                  className="page-shell min-h-screen text-gray-900"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <JourneyBuilderPage />
                </motion.div>
              }
            />

            <Route
              path="/journey-builder/plan/:planId"
              element={
                <SubscriptionGuard>
                  <motion.div
                    className="page-shell min-h-screen text-gray-900"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <JourneyPlanDetails />
                  </motion.div>
                </SubscriptionGuard>
              }
            />

            <Route
              path="/treatment-options"
              element={
                <motion.div
                  className="page-shell min-h-screen"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <TreatmentOptionsPage />
                </motion.div>
              }
            />

            {/* ----------- Company / Legal ----------- */}
            <Route
              path="/careers"
              element={
                <motion.div
                  className="page-shell min-h-screen bg-cream-paper"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <Careers />
                </motion.div>
              }
            />
            <Route
              path="/terms-and-conditions"
              element={
                <motion.div
                  className="page-shell min-h-screen bg-cream-paper"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <TermsAndConditions />
                </motion.div>
              }
            />
            <Route
              path="/privacy"
              element={
                <motion.div
                  className="page-shell min-h-screen bg-cream-paper"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <PrivacyPolicy />
                </motion.div>
              }
            />
            <Route
              path="/health-privacy"
              element={
                <motion.div
                  className="page-shell min-h-screen bg-cream-paper"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <ConsumerHealthPrivacy />
                </motion.div>
              }
            />
            <Route
              path="/medical-disclaimer"
              element={
                <motion.div
                  className="page-shell min-h-screen bg-cream-paper"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <MedicalDisclaimer />
                </motion.div>
              }
            />
            <Route
              path="/security"
              element={
                <motion.div
                  className="page-shell min-h-screen bg-cream-paper"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <Security />
                </motion.div>
              }
            />
            <Route
              path="/about"
              element={
                <motion.div
                  className="page-shell min-h-screen"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <AboutPage />
                </motion.div>
              }
            />

            <Route path="/pro-waitlist" element={<Navigate to="/subscribe" replace />} />

            {/* ----------- OncoAgent ----------- */}
            <Route
              path="/oncoagent"
              element={
                <OncoAgentErrorBoundary>
                  <motion.div
                    className="page-shell min-h-screen"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <OncoAgentLanding />
                  </motion.div>
                </OncoAgentErrorBoundary>
              }
            />
            <Route
              path="/oncoagent/new"
              element={
                <OncoAgentErrorBoundary>
                  <motion.div
                    className="page-shell min-h-screen"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <OncoAgentNewCase />
                  </motion.div>
                </OncoAgentErrorBoundary>
              }
            />
            {oncoAgentAppRoute()}
            {oncoAgentLegacyRedirects()}
            <Route
              path="/oncoagent/track/:shareToken"
              element={
                <OncoAgentErrorBoundary>
                  <motion.div
                    className="page-shell min-h-screen"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <OncoAgentTrack />
                  </motion.div>
                </OncoAgentErrorBoundary>
              }
            />
            <Route
              path="/oncoagent/case/:caseId"
              element={
                <OncoAgentErrorBoundary>
                  <motion.div
                    className="page-shell min-h-screen"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <OncoAgentDashboard />
                  </motion.div>
                </OncoAgentErrorBoundary>
              }
            />
            <Route
              path="/oncoagent/diagnostics"
              element={
                <Suspense
                  fallback={
                    <div className="min-h-screen bg-cream-paper flex items-center justify-center">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
                        <p className="text-gray-600">Loading diagnostics…</p>
                      </div>
                    </div>
                  }
                >
                  <motion.div
                    className="page-shell min-h-screen"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <OncoAgentDiagnostics />
                  </motion.div>
                </Suspense>
              }
            />

            {/* ----------- Razorpay-required ----------- */}
            <Route
              path="/cancellation-refund"
              element={
                <motion.div
                  className="page-shell min-h-screen bg-cream-paper"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <CancellationRefund />
                </motion.div>
              }
            />
            <Route
              path="/shipping-delivery"
              element={
                <motion.div
                  className="page-shell min-h-screen bg-cream-paper"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ShippingDelivery />
                </motion.div>
              }
            />

            {/* Catch-all 404 handler: prevents blank screens on unknown paths */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </GlobalErrorBoundary>
    </AnimatePresence>

      {isSpecialDay || isMemberApp || isOncoAgentApp ? null : <CookieConsent />}
      {isSpecialDay || isMemberApp || isOncoAgentApp ? null : <VaniSplash />}
    </>
  );
}
